import { FC } from 'react';
import { useParams } from 'next/navigation';
import { LineItem } from 'shared/types/cart/LineItem';
import Typography from 'components/commercetools-ui/atoms/typography';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import { useFormat } from 'helpers/hooks/useFormat';
import useLocale from 'helpers/hooks/useLocale';
import Image, { ImageProps } from 'frontastic/lib/image';

type OrderItemProps = {
  lineItem: LineItem;
  productLogo?: ImageProps;
};

const labelMapping = {
  Sex: 'Gender',
  IronSets: 'Set Make-Up',
  LoftAdj: 'Loft Adjustment',
  LieAdj: 'Lie Adjustment',
  LengthAdj: 'Length Adjustment',
  GripThickness: 'Grip Thickness',
};

const OrderItem: FC<OrderItemProps> = ({ lineItem, productLogo }) => {
  const { ctLocale: locale } = useLocale();
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const isIronsCategory = lineItem?.categoryName?.toLowerCase().includes('irons');
  const renderAdditionalInfo = () => {
    const { optionsOrder, ...attributes } = lineItem?.variant?.attributes || {};
    if (!optionsOrder || optionsOrder.length === 0) {
      return null;
    }

    if (Object.hasOwn(lineItem, 'selectedCustomFitOptions')) {
      return lineItem?.selectedCustomFitOptions?.map((optionKey: string) => {
        const value = optionKey.split(':');
        const mappedLabel = (labelMapping as any)[value[1]] || value[1];

        return (
          <div key={optionKey} className="mt-4">
            <span className="font-light capitalize">{mappedLabel}:</span>{' '}
            <span className="font-light capitalize">{value[2]}</span>
          </div>
        );
      });
    }

    const personalizedFields = Object.hasOwn(lineItem, 'personalizedTextFields')
      ? Object.entries(lineItem.personalizedTextFields || {}).map(([key, value]) => {
          const personalizedText = value.split(':');
          const label = personalizedText[0] ? personalizedText[0].trim() : '';
          const text = personalizedText[1] ? personalizedText[1].trim() : '';

          if (text) {
            return (
              <div key={key} className="mt-4">
                <span className="font-light capitalize">{label}:</span>{' '}
                <span className="font-light capitalize">{text}</span>
              </div>
            );
          }

          return null;
        })
      : [];

    const optionsFields = optionsOrder.map((optionKey: string) => {
      const optionLabel = formatCartMessage({
        id: optionKey.toLocaleLowerCase(),
        defaultMessage: optionKey.toLocaleLowerCase(),
      });
      const value = attributes[optionKey];
      if (!value) return null;
      return (
        <div key={optionKey} className="mt-4 font-normal">
          <span className="font-light capitalize">{optionLabel.replace(/_/g, ' ')}:</span>{' '}
          <span className="font-light capitalize">{value.charAt(0).toUpperCase() + value.slice(1)}</span>
        </div>
      );
    });

    return [...optionsFields, ...personalizedFields];
  };
  return (
    <div key={lineItem.lineItemId} className="flex justify-start border-b py-16">
      <div className="relative h-[104px] w-[88px] shrink-0">
        <Image
          fill
          src={lineItem?.variant?.images?.[0] || productLogo?.media?.file}
          style={{ objectFit: 'contain' }}
          alt={lineItem.name}
        />
      </div>
      <div className="flex flex-col justify-center pl-16">
        <Typography className="font-medium text-primary-black">{lineItem?.name}</Typography>
        <Typography className="mt-8 font-normal text-primary-black">
          {CurrencyHelpers.formatForCurrency(lineItem?.price as number, locale)}
        </Typography>
        {renderAdditionalInfo()}(
        <Typography className="mt-8 font-normal text-primary-black">{`x ${lineItem?.count}`}</Typography>)
      </div>
    </div>
  );
};

export default OrderItem;
